import type { PlanTier } from '../../lib/plan';
import type { AdminBusiness, BusinessCoverageStatus } from '../api/schemas';
import { statusBucket } from './status';

/**
 * The roster of businesses as the admin panel filters and counts it: no fetching
 * and no React. The roster page, the search of the top bar and the figures all
 * read through these, so a business is found and counted the same way everywhere.
 */

export const ADMIN_BUSINESSES_QUERY_KEY = ['admin-businesses'];

export type StatusFilter = 'todos' | BusinessCoverageStatus | 'prueba';
export type PlanFilter = 'todos' | PlanTier;

const DEFAULT_PLAN: PlanTier = 'base';

/** Case/accent-insensitive contains, so "barberia" finds "Barbería". */
export function normalizeText(value: string): string {
  return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

export function matchesSearch(business: Pick<AdminBusiness, 'name'>, search: string): boolean {
  if (search === '') return true;
  return normalizeText(business.name).includes(normalizeText(search));
}

export function planOf(business: AdminBusiness): PlanTier {
  return business.plan ?? DEFAULT_PLAN;
}

/** Running means customers can see and book it: approved and not suspended. */
export function isRunning(business: AdminBusiness): boolean {
  return !business.pendingApproval && !business.suspendedAt;
}

export function filterBusinesses(
  businesses: readonly AdminBusiness[],
  search: string,
  status: StatusFilter,
  plan: PlanFilter = 'todos',
  now: Date = new Date(),
): AdminBusiness[] {
  return businesses.filter(
    (business) =>
      matchesSearch(business, search.trim()) &&
      (status === 'todos' || statusBucket(business, now) === status) &&
      (plan === 'todos' || planOf(business) === plan),
  );
}

export type RosterFigures = { active: number; monthlyIncomeCents: number; pending: number; overdue: number };

export function rosterFigures(businesses: readonly AdminBusiness[], now: Date = new Date()): RosterFigures {
  const running = businesses.filter(isRunning);
  return {
    active: running.length,
    monthlyIncomeCents: running.reduce((sum, business) => sum + (business.monthlyPriceCents ?? 0), 0),
    pending: businesses.filter((business) => business.pendingApproval).length,
    // A business in its free trial owes nothing yet.
    overdue: businesses.filter((business) => statusBucket(business, now) === 'atrasado').length,
  };
}

export function countByPlan(businesses: readonly AdminBusiness[]): Record<PlanFilter, number> {
  const empty: Record<PlanFilter, number> = { todos: businesses.length, reservas: 0, base: 0, pro: 0, max: 0, ultra: 0 };
  return businesses.reduce((counts, business) => {
    const plan = planOf(business);
    return { ...counts, [plan]: counts[plan] + 1 };
  }, empty);
}

